import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from 'components/UnvUI'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import styles from './PostgradPage.module.scss'

const emptyForm = {
  full_name: '',
  stage: 'master',
  supervisor: '',
  topic: '',
  defense_year: new Date().getFullYear(),
}

const PostgradPage = () => {
  const { t } = useTranslation('common')
  const STAGE_LABELS = {
    master: t('postgrad.stage_master'),
    phd: t('postgrad.stage_phd'),
  }
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [me, setMe] = useState(null)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const isHr = useMemo(() => {
    if (!me) return false
    const roles = me.roles || []
    return roles.includes('hr') || roles.includes('hr_students') || me.primary_role === 'hr'
  }, [me])

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/students/postgrad/')
      setList(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  const loadMe = async () => {
    try {
      const res = await $api.get('/admin-panel/me/')
      setMe(res.data)
    } catch {
      setMe(null)
    }
  }

  useEffect(() => {
    loadMe()
    load()
  }, [])

  const openCreate = () => {
    setEditing({ id: null })
    setForm(emptyForm)
  }

  const openEdit = (row) => {
    setEditing(row)
    setForm({
      full_name: row.full_name || '',
      stage: row.stage || 'master',
      supervisor: row.supervisor || row.supervisor_name || '',
      topic: row.topic || '',
      defense_year: row.defense_year || new Date().getFullYear(),
    })
  }

  const save = async () => {
    try {
      if (editing?.id) {
        await $api.patch(`/students/postgrad/${editing.id}/`, form)
        setNotify({ isOpen: true, message: t('postgrad.saved'), type: 'success', sound: 'success' })
      } else {
        await $api.post('/students/postgrad/', form)
        setNotify({ isOpen: true, message: t('postgrad.added'), type: 'success', sound: 'success' })
      }
      setEditing(null)
      load()
    } catch {
      setNotify({ isOpen: true, message: t('postgrad.save_error'), type: 'error', sound: 'error' })
    }
  }

  const remove = async (row) => {
    if (!window.confirm(t('postgrad.delete_record_confirm'))) return
    try {
      await $api.delete(`/students/postgrad/${row.id}/`)
      setNotify({ isOpen: true, message: t('postgrad.deleted'), type: 'success', sound: 'success' })
      load()
    } catch {
      setNotify({ isOpen: true, message: t('postgrad.delete_error'), type: 'error', sound: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <h1 className={styles.title}>{t('postgrad.title_page')}</h1>
        {isHr && (
          <Button variant="contained" onClick={openCreate}>
            {t('common.add')}
          </Button>
        )}
      </div>

      <div className={`unv-card ${styles.content}`}>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        ) : error ? (
          <div className="unv-empty">{t('postgrad.section_in_dev')}</div>
        ) : list.length === 0 ? (
          <div className="unv-empty">{t('postgrad.no_records')}</div>
        ) : (
          <table className="unv-table">
            <thead>
              <tr>
                <th>{t('postgrad.col_full_name')}</th>
                <th>{t('postgrad.col_stage')}</th>
                <th>{t('postgrad.col_supervisor')}</th>
                <th>{t('postgrad.col_topic')}</th>
                <th>{t('postgrad.col_defense_year')}</th>
                {isHr && <th></th>}
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td>{r.full_name || '—'}</td>
                  <td>{STAGE_LABELS[r.stage] || r.stage || '—'}</td>
                  <td>{r.supervisor_name || r.supervisor || '—'}</td>
                  <td>{r.topic || '—'}</td>
                  <td>{r.defense_year || '—'}</td>
                  {isHr && (
                    <td>
                      <button className="unv-btn unv-btn--ghost unv-btn--sm" onClick={() => openEdit(r)}>
                        {t('postgrad.edit_btn')}
                      </button>{' '}
                      <button className="unv-btn unv-btn--danger unv-btn--sm" onClick={() => remove(r)}>
                        {t('common.delete')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? t('postgrad.modal_edit_record') : t('postgrad.modal_new_record')}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label={t('postgrad.field_full_name')}
            value={form.full_name}
            onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            margin="normal"
          />
          <TextField
            select
            fullWidth
            label={t('postgrad.field_stage')}
            value={form.stage}
            onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))}
            margin="normal"
          >
            <MenuItem value="master">{t('postgrad.stage_master')}</MenuItem>
            <MenuItem value="phd">{t('postgrad.stage_phd')}</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label={t('postgrad.field_supervisor')}
            value={form.supervisor}
            onChange={(e) => setForm((f) => ({ ...f, supervisor: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('postgrad.field_topic')}
            value={form.topic}
            onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            type="number"
            fullWidth
            label={t('postgrad.field_defense_year')}
            value={form.defense_year}
            onChange={(e) => setForm((f) => ({ ...f, defense_year: Number(e.target.value) }))}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={save}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default PostgradPage
